import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import { toast } from "react-toastify";
import moment from "moment";

import ExcelUpload from "../../../utils/components/excelUpload";
import { BASE_URL } from "../../../global";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(900px, 92vw)",
  maxHeight: "85vh",
  backgroundColor: "#fff",
  boxShadow: 24,
  borderRadius: "12px",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

// Excel header (label) -> internal key mapping. Only these columns are kept;
// anything else in the uploaded sheet is ignored.
const FIELD_MAP = [
  { label: "Master EQP Name", key: "standardName" },
  { label: "ID", key: "stId" },
  { label: "Serial Number", key: "serialNo" },
  { label: "Make", key: "make" },
  { label: "Model", key: "model" },
  { label: "Traceability", key: "certificateNumber" },
  { label: "Cal Agency", key: "traceability" },
  { label: "Calibration Date", key: "calDate" },
  { label: "Due Date", key: "validUpto" },
  { label: "Discipline", key: "disciplineId" },
  { label: "Range", key: "masterrange" },
  { label: "Accuracy", key: "masteraccuracy" },
  { label: "Least Count", key: "masterleastcount" },
  { label: "Uncertainty", key: "masterUncertainty" },
  { label: "Type", key: "type" },
  { label: "Material Type", key: "materialType" },
  { label: "Location", key: "location" },
];

// Keys whose value should only take the part before the first comma
// (e.g. "5,steel" -> "5", or "0.0000047,notes" -> "0.0000047").
const SPLIT_FIRST_COMMA_KEYS = new Set(["disciplineId", "materialType"]);

// Keys that should end up as numbers after splitting (needed so they can be
// compared/matched correctly, e.g. disciplineId against discipline.id).
const NUMERIC_KEYS = new Set(["disciplineId"]);

// Keys that need range/unit formatting via formatRangeValue
// (e.g. "0 to 20 bar" -> "0#bar|20#bar").
const FORMAT_RANGE_KEYS = new Set([
  "masterrange",
  "masteraccuracy",
  "masterleastcount",
  "masterUncertainty",
]);

// Normalizes a header for comparison: lowercase, strip punctuation
// (periods, underscores, extra spaces, etc.), collapse whitespace.
// Makes "Cal. Agency  " match "Cal Agency" and "traceability" match "traceability".
const normalizeHeader = (header) =>
  String(header ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** EXTENDED formatter as per requirement (FULL unit support: kg/cm², μS/cm, N·m, m/s, etc.) */
function formatRangeValue(value) {
  if (!value || typeof value !== "string") return value;

  if (value.includes("#")) return value;

  const addHashToUnit = (num, unit = "") =>
    unit ? `${num}#${unit.trim()}` : `${num}#`;

  const numPattern = "[\\d.⁰¹²³⁴⁵⁶⁷⁸⁹ᶟ]+";

  return value
    .split(",")
    .map((part) => {
      const cleaned = String(part).trim();

      if (!cleaned) return "";

      // RANGE SUPPORT (with optional spaces)
      const rangeRegex = new RegExp(
        `^(${numPattern})\\s*(.*?)\\s*to\\s*(${numPattern})\\s*(.*)$`,
        "i"
      );

      const rangeMatch = cleaned.match(rangeRegex);

      if (rangeMatch) {
        const fromVal = rangeMatch[1];
        const fromUnit = rangeMatch[2];
        const toVal = rangeMatch[3];
        const toUnit = rangeMatch[4];

        const unit = (fromUnit || toUnit || "").trim();

        return `${addHashToUnit(fromVal, unit)}|${addHashToUnit(toVal, unit)}`;
      }

      // TOLERANCE
      if (cleaned.includes("±")) {
        const tolMatch = cleaned.match(/^([\d.±]+)\s*(.*)$/);
        if (tolMatch) {
          return addHashToUnit(tolMatch[1], tolMatch[2]);
        }
      }

      // SINGLE VALUE
      const singleRegex = new RegExp(`^(${numPattern})\\s*(.*)$`);
      const singleMatch = cleaned.match(singleRegex);

      if (singleMatch) {
        return addHashToUnit(singleMatch[1], singleMatch[2]);
      }

      return cleaned + "#";
    })
    .filter(Boolean)
    .join("||");
}

// Remaps a raw Excel row (keyed by whatever headers were in the sheet) to
// only the keys defined in FIELD_MAP. Two-pass matching:
//   1. Label match is tried FIRST for every field, in FIELD_MAP order —
//      this is the authoritative mapping as written (e.g. "Traceability"
//      -> certificateNumber, "Cal Agency" -> traceability).
//   2. Only fields that found no label match fall back to an exact
//      key-name match, and only against headers not already claimed
//      in pass 1 — so a field's own key text can never steal a header
//      that another field already claimed by its label.
function mapExcelRow(rawRow) {
  const rawHeaders = Object.keys(rawRow);
  const usedHeaders = new Set();
  const fieldToHeader = new Map();

  // Pass 1: label match (authoritative — respects FIELD_MAP as written)
  FIELD_MAP.forEach(({ label, key }) => {
    const wantedLabel = normalizeHeader(label);
    const matchingHeader = rawHeaders.find(
      (header) => !usedHeaders.has(header) && normalizeHeader(header) === wantedLabel
    );
    if (matchingHeader !== undefined) {
      fieldToHeader.set(key, matchingHeader);
      usedHeaders.add(matchingHeader);
    }
  });

  // Pass 2: key-name match, only for fields still unresolved,
  // only against headers not already claimed in pass 1
  FIELD_MAP.forEach(({ key }) => {
    if (fieldToHeader.has(key)) return;
    const wantedKey = normalizeHeader(key);
    const matchingHeader = rawHeaders.find(
      (header) => !usedHeaders.has(header) && normalizeHeader(header) === wantedKey
    );
    if (matchingHeader !== undefined) {
      fieldToHeader.set(key, matchingHeader);
      usedHeaders.add(matchingHeader);
    }
  });

  // Build the mapped row, applying transforms per key
  const mapped = {};
  FIELD_MAP.forEach(({ key }) => {
    const matchingHeader = fieldToHeader.get(key);

    if (matchingHeader === undefined) {
      mapped[key] = null;
      return;
    }

    let value = rawRow[matchingHeader];

    if (SPLIT_FIRST_COMMA_KEYS.has(key) && typeof value === "string") {
      value = value.split(",")[0].trim();
    }

    if (NUMERIC_KEYS.has(key) && value !== null && value !== "") {
      const num = Number(value);
      value = isNaN(num) ? value : num;
    }

    if (FORMAT_RANGE_KEYS.has(key) && typeof value === "string") {
      value = formatRangeValue(value);
    }

    mapped[key] = value;
  });

  return mapped;
}

// Fill any keys missing from a row (relative to the full key set in the
// array) with null, so every row has a consistent shape downstream.
function fillMissingKeysWithNull(rows) {
  const allKeys = new Set();
  rows.forEach((row) => Object.keys(row).forEach((key) => allKeys.add(key)));
  return rows.map((row) => {
    const filled = {};
    allKeys.forEach((key) => {
      filled[key] = row[key] !== undefined ? row[key] : null;
    });
    return filled;
  });
}

// Builds a CSV blob from an array of objects and triggers a download.
function downloadAsCsv(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escapeCell = (val) => {
    const str = val === null || val === undefined ? "" : String(val);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const csv = [
    headers.join(","),
    ...rows.map((row) => headers.map((h) => escapeCell(row[h])).join(",")),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// Converts an Excel date serial number to a JS Date.
// Excel's epoch starts Dec 30, 1899; 25569 = days between Excel epoch and Unix epoch.
function excelSerialToDate(serial) {
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400; // seconds
  return new Date(utcValue * 1000);
}

// Only validUpto needs special handling since Excel gives dates as serial
// numbers. Every other key is left untouched, as-is, as a string.
function formatValidUpto(value) {
  if (value === null || value === undefined || value === "") return value;

  // Excel serial number case
  if (typeof value === "number") {
    return moment(excelSerialToDate(value)).format("YYYY-MM-DD");
  }

  // Already a Date object or date-like string
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
}

/**
 * Modal for bulk-importing standards from an Excel file.
 *
 * @param {(rows: object[]) => void} onImport - called with the accepted,
 *   null-filled rows when the user confirms the import.
 */
export const StandardImportExcelModal = ({ onImport }) => {
  const [open, setOpen] = useState(false);
  const [excelArray, setExcelArray] = useState([]);
  const [discipline, setDiscipline] = useState([]);
  const [loadingDiscipline, setLoadingDiscipline] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("rejected");

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setExcelArray([]);
    setActiveTab("rejected");
  };

  const fetchDisciplineData = () => {
    setLoadingDiscipline(true);
    axiosWithToken
      .get(BASE_URL + "discipline?_fields=id,name&_where=(status,eq,1)")
      .then((res) => setDiscipline(res.data))
      .catch((err) => {
        console.log("discipline data error: ", err);
        toast.error("Couldn't load disciplines. Please try again.");
      })
      .finally(() => setLoadingDiscipline(false));
  };

  useEffect(() => {
    if (open) fetchDisciplineData();
  }, [open]);

  // Derived instead of duplicated in state + effect: recomputes only when
  // the parsed file or the discipline list actually changes.
  const { acceptedRows, rejectedRows } = useMemo(() => {
    const accepted = [];
    const rejected = [];

    for (const rawRow of excelArray) {
      const row = mapExcelRow(rawRow);
      const found = discipline.find((i) => i.id == row.disciplineId);
      if (!found) {
        rejected.push({
          ...row,
          rejectionStatus: "Discipline not found in the database",
        });
      } else {
        accepted.push(row);
      }
    }

    return { acceptedRows: accepted, rejectedRows: rejected };
  }, [excelArray, discipline]);

  const hasData = excelArray.length > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const rowsToSubmit = acceptedRows.map((row) => ({
        ...row,
        validUpto: formatValidUpto(row.validUpto),
      }));

      await axiosWithToken.post(BASE_URL + "standards/bulk", fillMissingKeysWithNull(rowsToSubmit));
      toast.success(`Imported ${acceptedRows.length} standard${acceptedRows.length === 1 ? "" : "s"}.`);
      handleClose();
      window.location.reload(); // Refresh the page to reflect the new data
    } catch (error) {
      console.log("bulk import error:", error);
      toast.error(error?.response?.data?.message || "Import failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const rowsToShow = activeTab === "rejected" ? rejectedRows : acceptedRows;

  return (
    <div>
      <Button
        id="ducMaster_list_uploadexcel"
        variant="contained"
        onClick={handleOpen}
        size="small"
      >
        Upload Excel File
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 3,
              py: 2,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Import Standards from Excel
            </Typography>

            <IconButton
              onClick={handleClose}
              aria-label="Close dialog"
              size="small"
              sx={{
                backgroundColor: "action.hover",
                "&:hover": { backgroundColor: "error.light", color: "error.contrastText" },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider />

          {/* Body */}
          <Box sx={{ px: 3, py: 2, overflowY: "auto", flex: 1 }}>
            <ExcelUpload setExcelArray={setExcelArray} buttonName="Select Excel File" />

            {loadingDiscipline && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading disciplines…
                </Typography>
              </Stack>
            )}

            {hasData && !loadingDiscipline && (
              <>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 3, mb: 1 }}
                >
                  <Tabs
                    value={activeTab}
                    onChange={(_, val) => setActiveTab(val)}
                    sx={{ minHeight: 36 }}
                  >
                    <Tab
                      value="accepted"
                      sx={{ minHeight: 36, py: 0.5 }}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CheckCircleRoundedIcon fontSize="small" color="success" />
                          <span>Accepted</span>
                          <Chip size="small" label={acceptedRows.length} color="success" variant="outlined" />
                        </Stack>
                      }
                    />
                    <Tab
                      value="rejected"
                      sx={{ minHeight: 36, py: 0.5 }}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CancelRoundedIcon fontSize="small" color="error" />
                          <span>Rejected</span>
                          <Chip size="small" label={rejectedRows.length} color="error" variant="outlined" />
                        </Stack>
                      }
                    />
                  </Tabs>

                  {activeTab === "rejected" && rejectedRows.length > 0 && (
                    <Tooltip title="Download rejected rows as CSV">
                      <Button
                        size="small"
                        startIcon={<FileDownloadRoundedIcon fontSize="small" />}
                        onClick={() => downloadAsCsv(rejectedRows, "rejected-standards.csv")}
                      >
                        Export
                      </Button>
                    </Tooltip>
                  )}
                </Stack>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Sr.</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Discipline ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Standard Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Master EQP ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Serial No</TableCell>
                        {activeTab === "rejected" && (
                          <TableCell sx={{ fontWeight: 600 }}>Rejection Reason</TableCell>
                        )}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {rowsToShow.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={activeTab === "rejected" ? 6 : 5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                            No {activeTab} rows.
                          </TableCell>
                        </TableRow>
                      ) : (
                        rowsToShow.map((row, i) => (
                          <TableRow key={`${row.stId ?? "row"}-${i}`} hover>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{row.disciplineId}</TableCell>
                            <TableCell>{row.standardName}</TableCell>
                            <TableCell>{row.stId}</TableCell>
                            <TableCell>{row.serialNo}</TableCell>
                            {activeTab === "rejected" && (
                              <TableCell>
                                <Typography variant="body2" color="error.main">
                                  {row.rejectionStatus}
                                </Typography>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {!hasData && !loadingDiscipline && (
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ py: 6, color: "text.secondary" }}
              >
                <InboxRoundedIcon sx={{ fontSize: 40, opacity: 0.4 }} />
                <Typography variant="body2">
                  Select an Excel file to preview accepted and rejected rows.
                </Typography>
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {hasData
                ? `${acceptedRows.length} of ${excelArray.length} row${excelArray.length === 1 ? "" : "s"} ready to import`
                : ""}
            </Typography>

            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>

              <Button
                variant="contained"
                disabled={!acceptedRows.length || submitting}
                onClick={handleSubmit}
              >
                {submitting ? "Importing…" : `Import ${acceptedRows.length || ""}`}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};